import { IdType } from '@euler/model/common';
import { ReceptionOrderStatus } from '@euler/model/enum/ReceptionOrderStatus';

/**
 * Identity tuple user-presence ("online") is keyed by — mirrors the backend
 * `UserPresenceRef`. Structurally compatible with every chat identity (peer,
 * member, contact), so those can be passed straight through.
 */
export interface UserPresenceRef {
  platformUserId: IdType;
  appId: string;
  appScope: string;
}

/**
 * Denormalized order snapshot carried by an order-internal room (mirrors
 * the backend `OrderChatRoomSnapshot`). Rendered in the thread row's
 * order card; kept fresh by `thread.updated` (intent `room-info-updated`).
 */
export interface OrderChatRoomSnapshot {
  orderNo: string;
  /** ISO timestamp of order creation. */
  orderDate: string;
  mileage: number;
  customerName: string | null;
  /** Service advisor (order.service_agent_name); null when unassigned. */
  serviceAgentName: string | null;
  vehicleBrandName: string;
  vehicleBrandLogo: string | null;
  vehicleModel: string;
  /** Full canonical vehicle name. */
  vehicleName: string;
  status: ReceptionOrderStatus;
}

/**
 * Staff involved in an order (advisor + assigned technicians) — the
 * default selection for the order-room member picker.
 */
export interface OrderInvolvedMember {
  memberId: number;
  name: string;
  avatarUrl: string | null;
  /** Job title (org_member.job_title); optional, often empty. */
  jobTitle: string | null;
  /** Comma-separated OrgUserRoleType codes (org_user.role); always set. */
  role: string;
}

export interface ChatContact {
  memberId: number;
  name: string;
  avatar: string | null;
  jobTitle: string | null;
  /** Comma-separated OrgUserRoleType codes (org_user.role); always set. */
  role: string;
  mobile: string | null;
  platformUserId: IdType;
  appId: string;
  appScope: string;
}

export interface ChatThreadItem {
  id: string;
  room: {
    id: string;
    type: ChatRoomType;
    groupChatType: ChatGroupChatType | null;
    /** Custom (owner-set) group title; null when unnamed. */
    title: string | null;
    /** Auto member-name title; group fallback when `title` is null. */
    defaultTitle: string | null;
    avatarUrl: string | null;
    customerName: string | null;
    licensePlateNo: string | null;
    orderNo: string | null;
    /** Order-internal-room snapshot; null for other room types. */
    orderSnapshot: OrderChatRoomSnapshot | null;
  };
  /**
   * Private-chat counterpart snapshot — the other member's identity,
   * stamped on the thread at creation. Renders a private row's title +
   * avatar without loading members/contacts. Null for group threads.
   */
  peer: {
    platformUserId: IdType;
    appId: string;
    appScope: string;
    name: string | null;
    avatarUrl: string | null;
  } | null;
  latestMessage?: {
    id: string;
    preview: string | null;
    authorName: string | null;
    /**
     * Full structured payload of the latest message — render the row from
     * this (audio duration, file badge, …), not just `preview`.
     */
    payload?: ChatMessagePayload;
    createdAt: string;
  } | null;
  unreadCount: number;
  unreadMentionCount: number;
  latestActivityAt: string;
  mutedAt?: string | null;
  pinnedAt?: number | string | null;
  archivedAt?: string | null;
}

export interface ChatThreadListResult {
  items: ChatThreadItem[];
  nextCursor: string | null;
}

export interface ChatQuoteSnapshot {
  author: {
    type: string;
    role: string | null;
    id: IdType;
    name: string;
    avatar?: string;
    appId: string;
    appScope: string;
  };
  payloadType: string;
  preview: string;
  /**
   * Full payload of the quoted message → render the quote richly (image
   * thumbnail, card, …), falling back to `preview` when absent (snapshots
   * written before this field existed).
   */
  payload?: ChatMessagePayload;
  createdAt: string;
}

/**
 * Aggregated reactions on a message (mirrors the server's
 * `ChatMessageReactionSummary`): one entry per emoji, oldest first.
 */
export interface ChatMessageReactionSummary {
  emoji: string;
  count: number;
  /** Whether the viewer themselves reacted with this emoji. */
  reactedByMe: boolean;
}

/**
 * The raw message row returned by `chat/threads/:id/messages` (mirrors the
 * server's `ChatThreadMessageItem`). This is the API DTO — convert it to a
 * `RemoteChatMsg` (the domain shape) at the boundary via
 * `remoteChatMsgFromItem`. `id` is the recipient-row id (`mrc_…`); the FE
 * paginates on the response's `nextCursor`, not on this.
 */
export interface ChatThreadMessageItem {
  id: string;
  messageId: string;
  roomId: string;
  authorId: IdType;
  authorType: string;
  authorRole?: string | null;
  authorName?: string | null;
  authorAvatar?: string | null;
  payloadType: string;
  payload: ChatMessagePayload;
  quoteMessageId?: string | null;
  quoteSnapshot?: ChatQuoteSnapshot | null;
  editedAt?: string | null;
  deletedAt?: string | null;
  createdAt: string;
  readAt?: string | null;
  audioPlayedAt?: string | null;
  readByCount?: number;
  reactions?: ChatMessageReactionSummary[];
}

/**
 * A chat message in the conversation list — a discriminated union so the
 * type itself says whether it's a server-confirmed message or a local
 * optimistic one, instead of a bag of maybe-present fields.
 *
 *   - `remote`: a confirmed `chat_message` — has a `messageId`
 *     (`chat_message.id`) and the server-owned fields (edits, read state).
 *   - `local`: an optimistic message being sent (or that failed) — has only
 *     a `clientMessageId`; there is no `chat_message.id` yet. A successful
 *     send replaces it with a `remote` one (that IS the reconcile).
 */
interface ChatMsgBase {
  roomId: string;
  authorId: IdType;
  authorType: string;
  authorRole?: string | null;
  authorName?: string | null;
  authorAvatar?: string | null;
  payloadType: string;
  payload: ChatMessagePayload;
  quoteMessageId?: string | null;
  quoteSnapshot?: ChatQuoteSnapshot | null;
  createdAt: string;
}

export interface LocalChatMsg extends ChatMsgBase {
  kind: 'local';
  /**
   * Client-generated, room-unique id stamped on the optimistic send — its
   * identity and dedup key until the server confirms it (then it's replaced
   * by a `remote` message keyed on `messageId`).
   */
  clientMessageId: string;
  status: 'sending' | 'failed';
  /** Failure detail, for the `failed` state. */
  error?: string;
}

export interface RemoteChatMsg extends ChatMsgBase {
  kind: 'remote';
  /** `chat_message.id` — the canonical message identity + dedup key. */
  messageId: string;
  /**
   * The viewer's recipient-row id (`mrc_…`). Server-provided on both the
   * list DTO and the `message.new` envelope, so it's always present on a
   * remote message — used to anchor a context-window fetch (jump-to-quoted).
   */
  id: string;
  editedAt?: string | null;
  deletedAt?: string | null;
  /** The VIEWER's own read time (null/absent = unread by me). */
  readAt?: string | null;
  /** The VIEWER's own play time for an audio message (null = unplayed). */
  audioPlayedAt?: string | null;
  /**
   * Read-receipt for the viewer's OWN messages: how many OTHER members have
   * read it. > 0 ⇒ render the double-check (read) state.
   */
  readByCount?: number;
  /**
   * FE-accumulated "read by" list — filled live from `message.read` events
   * and/or an on-demand fetch. Drives the "who read" viewer.
   */
  readers?: ChatMessageReader[];
  /** Aggregated reactions (one per emoji); updated live from `reaction.changed`. */
  reactions?: ChatMessageReactionSummary[];
}

export type ChatMsg = LocalChatMsg | RemoteChatMsg;

/**
 * Stable, collision-free React/list key for a chat message. The `remote_` /
 * `local_` prefix keeps the two id spaces from ever colliding.
 */
export const keyOfChatMsg = (m: ChatMsg): string =>
  m.kind === 'remote' ? `remote_${m.messageId}` : `local_${m.clientMessageId}`;

/** One reader of a message (mirrors backend `ChatMessageReader`). */
export interface ChatMessageReader {
  platformUserId: IdType;
  appId: string;
  appScope: string;
  name: string | null;
  avatar: string | null;
  readAt: string;
}

export interface ChatMessageReadersResult {
  messageId: string;
  readers: ChatMessageReader[];
}

export interface ChatThreadMessagesResult {
  items: ChatThreadMessageItem[];
  nextCursor: string | null;
}

/**
 * Resolution of a conversation entry (a roomId or a private peer) into the
 * viewer's existing thread — an O(1) keyed lookup on the server. `null` when
 * no room exists for that entry yet (a first-contact private chat → the
 * client opens a bare room). `threadId` may be '' when the room exists but
 * has no thread yet (a freshly-created group with no messages).
 */
export interface ChatThreadResolution {
  threadId: string;
  roomId: string;
  roomType: ChatRoomType;
  title: string;
}

/** Conversation-entry resolution query — see backend `ResolveChatThreadQuery`.
 * `member` lets a caller that only knows an org member id (a task assignee)
 * start a chat; the server resolves the member → peer. */
export type ResolveChatThreadQuery =
  | { kind: 'room'; roomId: string }
  | { kind: 'peer'; peerPlatformUserId: IdType }
  | { kind: 'member'; orgMemberId: number };

/** The resolved private counterpart for the `member` query kind, so the client
 * can open a bare chat when no room exists yet. */
export interface ChatResolvedPeer {
  platformUserId: IdType;
  appId: string;
  appScope: string;
  name: string | null;
  avatar: string | null;
}

export interface ResolveChatThreadResult {
  thread: ChatThreadResolution | null;
  peer: ChatResolvedPeer | null;
}

export interface ChatUnreadCounts {
  [bucket: string]: number;
  __all__: number;
}

export interface ChatMessageWriteResult {
  messageId: string;
  roomId: string;
  threadId: string;
}

export interface ChatRoomActiveMember {
  id: string;
  platformUserId: IdType;
  appId: string;
  appScope: string;
  /** Member kind ('org_member' | 'customer' | 'bot'); bots render the
   * default 服务助手 avatar. */
  type?: string | null;
  /** `chat_bot.id` for bot members (empty/absent for humans); used to
   * build a `{kind:'bot'}` mention target so @-ing the bot wakes it. */
  botId?: string | null;
  /** Raw display-name snapshot from the backend member row. */
  name?: string | null;
  /** Resolved name for render (snapshot → contact fallback). */
  displayName: string;
  avatarUrl?: string | null;
  joinedAt: string;
  leftAt?: string | null;
}

export interface ChatRoomDetail {
  id: string;
  type: ChatRoomType;
  groupChatType?: ChatGroupChatType | null;
  title?: string | null;
  avatarUrl?: string | null;
  customerName?: string | null;
  licensePlateNo?: string | null;
  orderNo?: string | null;
  vin?: string | null;
  ownerUserId?: IdType | null;
  ownerAppId?: string | null;
  ownerAppScope?: string | null;
  dismissedAt?: string | null;
  createdAt: string;
  members: ChatRoomActiveMember[];
  /** Caller's thread id for this room; null when no thread row yet (mute/pin
   * can't be persisted until the first message). */
  threadId?: string | null;
  /** Caller's per-thread settings, for the detail toggles. */
  muted?: boolean;
  pinned?: boolean;
  /** Whether the caller is an active member. A same-store non-member still
   * gets the detail (member list + a "join" CTA); false here means the
   * conversation is gated and the FE shows the join button. */
  viewerIsMember?: boolean;
}

export interface ChatRoomMemberInput {
  type: 'org_member';
  memberId: number;
}

export interface CreateChatRoomResult {
  roomId: string;
  threadId: string;
  memberCount: number;
}

export type ChatRoomType = 'private' | 'group';
export type ChatGroupChatType =
  | 'default'
  | 'customer_service'
  | 'order-internal';

/**
 * A `@<user>` / `@<bot>` / `@all` callout inside a text-style payload.
 * Mirrors backend `ChatMentionTarget`. The triple identifies the human
 * target; `all` expands server-side to every active member (sans author).
 */
export type ChatMentionTarget =
  | { kind: 'user'; platformUserId: IdType; appId: string; appScope: string }
  | { kind: 'bot'; botId: string }
  | { kind: 'all' };

/** Mirrors backend `ChatMessageMention`. */
export interface ChatMessageMention {
  /** UTF-16 code-unit offset range within the carrier text/markdown. */
  range: [number, number];
  target: ChatMentionTarget;
  /** Display-name snapshot at compose time (renders the chip). */
  displayName: string;
}

export interface ChatMessagePayloadText {
  type: 'text';
  text: string;
  /** Per-occurrence mention tags; order matches occurrences in `text`. */
  mentions?: ChatMessageMention[];
}

export interface ChatMessagePayloadMarkdown {
  type: 'markdown';
  markdown: string;
  /** Per-occurrence mention tags; order matches occurrences in `markdown`. */
  mentions?: ChatMessageMention[];
}

export type ChatMediaKind = 'image' | 'video';

/** Single media artifact; mirrors backend `ChatMediaItem`. */
export interface ChatMediaItem {
  kind: ChatMediaKind;
  url: string;
  /** Poster/thumbnail URL; required for videos, optional for images. */
  coverUrl?: string;
  width?: number;
  height?: number;
  /** Duration in milliseconds; videos only. */
  durationMs?: number;
}

export interface ChatMessagePayloadMedia extends ChatMediaItem {
  type: 'media';
}

/** Bubble layout for a multi-item album; absent renders as `stack`. */
export type ChatMediasLayout = 'stack' | 'grid';

export interface ChatMessagePayloadMedias {
  type: 'medias';
  items: ChatMediaItem[];
  layout?: ChatMediasLayout;
}

export interface ChatMessagePayloadAudio {
  type: 'audio';
  url: string;
  durationMs: number;
  transcript?: string;
  waveform?: number[];
}

export interface ChatMessagePayloadFile {
  type: 'file';
  url: string;
  filename: string;
  mimeType: string;
  sizeInBytes: number;
}

/**
 * Task cards (order / service-record / report) carry STRUCTURED DATA,
 * never pre-rendered display strings (no `title` / `subtitle`). The
 * renderer composes its own title/body/badges via `describeChatCard` so
 * the card UI can be restyled without a payload change. Mirrors backend
 * `ChatCardVehicleInfo` / `ChatTaskCardPayloadBase`.
 */
export interface ChatCardVehicleInfo {
  /** Canonical full name (brand + series + year + trim). */
  name?: string;
  licensePlateNo?: string;
  vin?: string;
  brandName?: string;
  brandLogo?: string;
  manufacturer?: string;
  model?: string;
  modelYear?: number;
  fuelType?: string;
  displacement?: string;
  trimLevel?: string;
  mileage?: number;
  imageUrl?: string;
}

/** Status palette key — mirrors the task-card `StatusPaletteKey`. */
export type ChatCardStatusTone =
  | 'blue'
  | 'cyan'
  | 'green'
  | 'gray'
  | 'amber'
  | 'red'
  | 'violet';

/** Point-in-time task status snapshot (accent bar + badge). */
export interface ChatCardStatusInfo {
  label: string;
  tone: ChatCardStatusTone;
}

export interface ChatTaskCardBase {
  orgId: number;
  storeId: number;
  orderNo: string;
  /** Task no (order↔task 1:1) — deep-links to task detail. */
  taskNo?: string;
  /** Report no for the order. */
  reportNo?: string;
  customerName?: string;
  /** Service advisor org-member id + display name snapshot. */
  serviceAgentId?: number;
  serviceAgentName?: string;
  /** Order create / finish time (ISO 8601) snapshots — 建单 / 在厂 / 耗时. */
  orderedAt?: string;
  finishedAt?: string;
  /** Workflow status snapshot at share time (accent bar + badge). */
  status?: ChatCardStatusInfo;
  vehicle?: ChatCardVehicleInfo;
}

export interface ChatMessagePayloadOrderCard extends ChatTaskCardBase {
  type: 'order_card';
}

export interface ChatMessagePayloadServiceRecordCard extends ChatTaskCardBase {
  type: 'service_record_card';
  /** Service-record row id — deep-links to the record page (required). */
  serviceRecordId: string;
}

export interface ChatMessagePayloadReportCard extends ChatTaskCardBase {
  type: 'report_card';
  /** Report business no — identifies the report to open (required). */
  reportNo: string;
  /** ReportContentType numeric enum value. */
  reportType: number;
}

export interface ChatMessagePayloadContactCard {
  type: 'contact_card';
  platformUserId: IdType;
  appId: string;
  appScope: string;
  name: string;
  avatar?: string;
  title?: string;
  phone?: string;
}

export interface ChatMembersInvitedPayload {
  type: 'notification';
  notificationType: 'members.invited';
  inviterUserId: IdType;
  inviterName: string;
  invitees: Array<{ platformUserId: IdType; name: string }>;
  invitedAt: string;
  _s?: string;
}

export interface ChatMembersJoinedPayload {
  type: 'notification';
  notificationType: 'members.joined';
  joinerUserId: IdType;
  joinerName: string;
  joinedAt: string;
  _s?: string;
}

export interface ChatRoomDismissedPayload {
  type: 'notification';
  notificationType: 'room.dismissed';
  ownerUserId: IdType;
  ownerName: string;
  roomTitle?: string;
  dismissedAt: string;
  _s?: string;
}

export interface ChatMembersRemovedPayload {
  type: 'notification';
  notificationType: 'members.removed';
  removerUserId: IdType;
  removerName: string;
  removed: Array<{
    platformUserId: IdType;
    appId: string;
    appScope: string;
    name: string;
  }>;
  removedAt: string;
  _s?: string;
}

export interface ChatTitleChangedPayload {
  type: 'notification';
  notificationType: 'title.changed';
  changerUserId: IdType;
  changerName: string;
  /** New custom title; null = reset to the member-name default. */
  title: string | null;
  changedAt: string;
  _s?: string;
}

export type OrderInfoChangeField =
  | 'licensePlateNo'
  | 'vehicleMileage'
  | 'customerName'
  | 'status';

export interface ChatOrderInfoUpdatedPayload {
  type: 'notification';
  notificationType: 'order.info_updated';
  field: OrderInfoChangeField;
  value: string | number | null;
  changedAt: string;
  /** `status`-change only. */
  isRollback?: boolean;
  rollbackReason?: string;
  _s?: string;
}

export type ChatNotificationPayload =
  | ChatMembersInvitedPayload
  | ChatMembersJoinedPayload
  | ChatRoomDismissedPayload
  | ChatMembersRemovedPayload
  | ChatTitleChangedPayload
  | ChatOrderInfoUpdatedPayload;

/** Payload variants a user/bot author can send (excludes notifications). */
export type ChatUserMessagePayload =
  | ChatMessagePayloadText
  | ChatMessagePayloadMarkdown
  | ChatMessagePayloadMedia
  | ChatMessagePayloadMedias
  | ChatMessagePayloadAudio
  | ChatMessagePayloadFile
  | ChatMessagePayloadOrderCard
  | ChatMessagePayloadServiceRecordCard
  | ChatMessagePayloadReportCard
  | ChatMessagePayloadContactCard;

export type ChatMessagePayload =
  | ChatUserMessagePayload
  | ChatNotificationPayload;

export interface InviteRoomMembersResult {
  results: Array<{
    memberRowId: string;
    reactivated: boolean;
    newlyAdded: boolean;
  }>;
}
